/**
 * Coordination entre usage « premier plan » (une action de l'utilisateur —
 * analyser sa partie, vérifier un coup d'exercice) et « arrière-plan » (la
 * file d'analyse Chess.com, cf. coaching/analysisQueue.ts) de l'instance
 * moteur partagée. `StockfishEngine.analyze()` annule silencieusement toute
 * requête en cours dès qu'une nouvelle démarre : sans coordination, une
 * analyse d'arrière-plan qui démarre pendant que l'utilisateur vérifie un
 * coup d'exercice ferait échouer sa propre requête.
 *
 * Règle : le premier plan a toujours la priorité et peut interrompre
 * l'arrière-plan à tout moment ; l'arrière-plan attend que le premier plan
 * soit inactif avant de démarrer quoi que ce soit.
 */

let activeBackgroundAbort: (() => void) | null = null;
let foregroundCount = 0;
const idleListeners = new Set<() => void>();

/** Enregistre le contrôleur d'annulation de la tâche d'arrière-plan en
 * cours, pour qu'une tâche de premier plan puisse l'interrompre. */
export function registerBackground(controller: AbortController): void {
  activeBackgroundAbort = () => controller.abort();
}

/** À appeler une fois la tâche d'arrière-plan terminée (succès, échec ou annulation). */
export function clearBackground(): void {
  activeBackgroundAbort = null;
}

export function isForegroundActive(): boolean {
  return foregroundCount > 0;
}

/** S'abonne aux moments où le premier plan redevient inactif (utilisé par
 * l'arrière-plan pour savoir quand retenter). Retourne une fonction de désabonnement. */
export function onIdle(cb: () => void): () => void {
  idleListeners.add(cb);
  return () => idleListeners.delete(cb);
}

/**
 * Exécute `fn` en tant que tâche de premier plan : interrompt d'abord toute
 * tâche d'arrière-plan en cours, puis empêche toute nouvelle tâche
 * d'arrière-plan de démarrer tant que `fn` n'est pas terminée.
 */
export async function runForeground<T>(fn: () => Promise<T>): Promise<T> {
  activeBackgroundAbort?.();
  foregroundCount += 1;
  try {
    return await fn();
  } finally {
    foregroundCount -= 1;
    if (foregroundCount === 0) {
      for (const cb of idleListeners) cb();
    }
  }
}

/** Réservé aux tests : remet le module à son état initial entre deux tests. */
export function resetEngineGateForTests(): void {
  activeBackgroundAbort = null;
  foregroundCount = 0;
  idleListeners.clear();
}
